import { useMemo } from "react";
import { Box, Typography } from "@mui/material";
import type { FormikProps } from "formik";
import { useTranslation } from "react-i18next";

import type {
  DynamicOptionsList,
  FormBuilderField,
  IncidentWorkItemWithContext,
  OptionsList,
} from "@thalamos/vision-common";
import { buildTranslationInput } from "@thalamos/vision-common";

import { useUser } from "../../Context/UserContext.js";
import { Banner } from "../Banner/Banner.js";
import { Translation } from "../Translation/Translation.js";
import { Checkbox } from "./Checkbox/Checkbox.js";
import { DatePicker } from "./DatePicker/DatePicker.js";
import { DateTimePicker } from "./DateTimePicker/DateTimePicker.js";
import { Dropdown } from "./Dropdown/Dropdown.js";
import IncidentDuration from "./IncidentDuration/IncidentDuration.js";
import { LocationPicker } from "./LocationPicker/LocationPicker.js";
import { MentalCapacityTable } from "./MentalCapacityTable/MentalCapacityTable.js";
import { MultiSelect } from "./MultiSelect/MultiSelect.js";
import { OfficerLookup } from "./OfficerLookup/OfficerLookup.js";
import { OrganisationDropdown } from "./OrganisationDropdown/OrganisationDropdown.js";
import { OrganisationTypeahead } from "./OrganisationTypeahead/OrganisationTypeahead.js";
import { PlaceOfSafetyPicker } from "./PlaceOfSafetyPicker/PlaceOfSafetyPicker.js";
import { RadioButtonGroup } from "./RadioButton/RadioButton.js";
import { Switch } from "./Switch/Switch.js";
import { TextField } from "./TextField/TextField.js";

export type FieldProps<
  Form extends Record<string, unknown>,
  Field extends keyof Form & string,
> = {
  field: FormBuilderField<Form, Field>;
  isEditable: boolean;
  incidentWorkItem?: IncidentWorkItemWithContext;
} & FormikProps<Form>;

const extractOptions = (
  options: OptionsList,
  args: Parameters<DynamicOptionsList>[0],
) => {
  const staticList = typeof options === "function" ? options(args) : options;
  return staticList.map((option) => ({
    label: buildTranslationInput(option.label, args),
    value: option.value,
  }));
};

export function Field<
  Form extends Record<string, unknown>,
  Field extends keyof Form & string,
>({
  field,
  values,
  isEditable,
  incidentWorkItem,
  autoFocus,
}: FieldProps<Form, Field> & { autoFocus?: boolean }) {
  const { t } = useTranslation();
  const { user } = useUser();

  const fieldContext = useMemo(
    () => ({
      t,
      formValues: values,
      user,
      incidentWorkItem,
    }),
    [t, user, values, incidentWorkItem],
  );

  // Every time the value changes, consider changing the values of dependent fields
  switch (field.type) {
    case "date":
      return (
        <DatePicker
          fieldName={field.fieldName}
          label={buildTranslationInput(field.label, fieldContext)}
          disablePast={field.disablePast}
          disableFuture={field.disableFuture}
          canBeUnknown={field.canBeUnknown}
          autoFocus={autoFocus}
        />
      );

    case "date-time":
      return (
        <DateTimePicker
          fieldName={field.fieldName}
          dateLabel={buildTranslationInput(field.dateLabel, fieldContext)}
          timeLabel={buildTranslationInput(field.timeLabel, fieldContext)}
          disablePast={field.disablePast}
          disableFuture={field.disableFuture}
          canBeUnknown={field.canBeUnknown}
          isEditable={isEditable}
          autoFocus={autoFocus}
        />
      );
    case "textbox":
      return (
        <TextField
          fieldName={field.fieldName}
          label={buildTranslationInput(field.label, fieldContext)}
          placeholder={buildTranslationInput(field.placeholder, fieldContext)}
          tooltip={
            field.tooltip
              ? {
                  title: buildTranslationInput(
                    field.tooltip.title,
                    fieldContext,
                  ),

                  content: buildTranslationInput(
                    field.tooltip.content,
                    fieldContext,
                  ),
                }
              : undefined
          }
          canBeUnknown={field.canBeUnknown}
          multiline={field.multiline}
          rows={field.rows}
          type={field.inputType}
          autoFocus={autoFocus}
        />
      );
    case "location":
      return (
        <LocationPicker
          fieldName={field.fieldName}
          label={buildTranslationInput(field.label, fieldContext)}
          canBeUnknown={field.canBeUnknown}
          autoFocus={autoFocus}
        />
      );
    case "checkbox":
      return (
        <Checkbox
          fieldName={field.fieldName}
          label={buildTranslationInput(field.label, fieldContext)}
          isEditable={isEditable}
          autoFocus={autoFocus}
        />
      );
    case "switch":
      return (
        <Switch
          fieldName={field.fieldName}
          label={buildTranslationInput(field.label, fieldContext)}
          autoFocus={autoFocus}
        />
      );
    case "typography":
      return (
        <Box>
          {field.label !== undefined && (
            <Typography sx={{ fontWeight: "bold", marginBottom: "0.5rem" }}>
              <Translation
                {...buildTranslationInput(field.label, fieldContext)}
              />
            </Typography>
          )}
          {field.textFn !== undefined &&
            field
              .textFn({ formValues: values, t })
              .map((value, index) => (
                <Typography key={index}>{value}</Typography>
              ))}
        </Box>
      );
    case "organisation":
      return (
        <OrganisationDropdown
          fieldName={field.fieldName}
          label={buildTranslationInput(field.label, fieldContext)}
          filterByRoles={field.filterByRoles}
          includeExcludedAsIncidentLocation={
            field.includeExcludedAsIncidentLocation
          }
          autoFocus={autoFocus}
        />
      );
    case "placeOfSafety":
      return (
        <PlaceOfSafetyPicker
          fieldName={field.fieldName}
          label={buildTranslationInput(field.label, fieldContext)}
          incidentWorkItem={incidentWorkItem}
          filter={field.filter?.(fieldContext)}
          autoFocus={autoFocus}
        />
      );
    case "dropdown":
      return (
        <Box>
          <Dropdown
            fieldName={field.fieldName}
            label={buildTranslationInput(field.label, fieldContext)}
            options={extractOptions(field.options, {
              incidentWorkItem,
              formValues: values,
              user,
              t,
            })}
            canBeUnknown={field.canBeUnknown}
            isEditable={isEditable}
            autoFocus={autoFocus}
          />
        </Box>
      );
    case "radio":
      return (
        <RadioButtonGroup
          fieldName={field.fieldName}
          label={buildTranslationInput(field.label, fieldContext)}
          options={extractOptions(field.options, {
            incidentWorkItem,
            formValues: values,
            user,
            t,
          })}
          canBeUnknown={field.canBeUnknown}
          autoFocus={autoFocus}
        />
      );
    case "banner":
      return (
        <Box>
          <Banner
            bannerType={field.bannerType}
            title={buildTranslationInput(field.title, fieldContext)}
            body={buildTranslationInput(field.body, fieldContext)}
            expandable={field.expandable}
          />
        </Box>
      );

    case "multi-select":
      return (
        <MultiSelect
          fieldName={field.fieldName}
          label={buildTranslationInput(field.label, fieldContext)}
          options={field.options}
          autoFocus={autoFocus}
        />
      );

    case "organisationTypeahead":
      return (
        <OrganisationTypeahead
          fieldName={field.fieldName}
          label={buildTranslationInput(field.label, fieldContext)}
          filterByRoles={field.filterByRoles}
          includeExcludedAsIncidentLocation={
            field.includeExcludedAsIncidentLocation
          }
          autoFocus={autoFocus}
        />
      );
    case "incident-duration":
      return (
        <IncidentDuration
          label={buildTranslationInput(field.label, fieldContext)}
          dateTimeLeft={field.getDateTimeLeft({
            formValues: values,
            incidentWorkItem,
          })}
          arrivalAtFirstPlaceOfSafetyDateTime={field.getArrivalAtFirstPlaceOfSafetyDateTime(
            {
              formValues: values,
              incidentWorkItem,
            },
          )}
          incidentWorkItem={incidentWorkItem}
        />
      );
    case "mental-capacity-table":
      return <MentalCapacityTable formValues={values} />;

    case "officerLookup":
      return (
        <OfficerLookup
          fieldName={field.fieldName}
          label={buildTranslationInput(field.label, fieldContext)}
          placeholder={buildTranslationInput(field.placeholder, fieldContext)}
          autoFocus={autoFocus}
        />
      );
  }
  return null;
}
